import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * Local draft storage (IndexedDB): edits not yet accepted by the server, always kept so an audit can be
 * continued without a connection. Records carry actor, tenant, resource, base
 * revision and an operation id and are never replayed across users or tenants. They have no expiry: unsent
 * work is never dropped silently. A record is removed once the server accepted it, and everything is removed
 * on sign-out (after the person confirmed) and when another account signs in on this device.
 */
export interface LocalDraftOperation {
    operationId: string;
    actorId: string;
    organizationId: string;
    workspaceId: string;
    auditId: string;
    resource: 'answer' | 'remark' | 'context';
    resourceKey: string;
    baseRevision: number;
    payload: unknown;
    updatedAt: number;
}

interface DraftsDb extends DBSchema {
    operations: { key: string; value: LocalDraftOperation; indexes: { audit: string } };
}

const dbName = 'hlt-local-drafts';

/** One connection per call, closed when it completes, so deleting the database is never blocked by this tab. */
async function withDb<T>(fn: (db: IDBPDatabase<DraftsDb>) => Promise<T>): Promise<T> {
    const db = await openDB<DraftsDb>(dbName, 1, {
        upgrade: (database) =>
            database.createObjectStore('operations', { keyPath: 'operationId' }).createIndex('audit', 'auditId'),
    });
    try {
        return await fn(db);
    } finally {
        db.close();
    }
}

export async function saveLocalOperation(op: LocalDraftOperation): Promise<void> {
    await withDb(async (db) => await db.put('operations', op));
}

/** Operations for one audit of one actor/workspace. */
export async function loadLocalOperations(
    actorId: string,
    organizationId: string,
    workspaceId: string,
    auditId: string,
): Promise<LocalDraftOperation[]> {
    const all = await withDb(async (db) => await db.getAllFromIndex('operations', 'audit', auditId));
    return all.filter(
        (op) => op.actorId === actorId && op.organizationId === organizationId && op.workspaceId === workspaceId,
    );
}

export async function deleteLocalOperation(operationId: string): Promise<void> {
    await withDb(async (db) => await db.delete('operations', operationId));
}

/** Unsent drafts stored on this device (all accounts), counted before sign-out removes them. */
export async function countLocalDrafts(): Promise<number> {
    try {
        return await withDb(async (db) => await db.count('operations'));
    } catch {
        // Storage unavailable (private mode or blocked): nothing is kept on this device.
        return 0;
    }
}

/**
 * How long a blocked deletion may wait for the other connection to close. A page this tab just left can still
 * hold a connection for a moment (the browser's back/forward cache closes it when the deletion asks).
 */
const blockedGraceMs = 3_000;

/** Delete every local draft; rejects when the browser could not delete them (for example another tab holds them). */
export async function clearLocalDrafts(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const blocked = () => {
            timer ??= setTimeout(() => reject(new Error('Local drafts are still open in another tab')), blockedGraceMs);
        };
        deleteDB(dbName, { blocked }).then(
            () => {
                clearTimeout(timer);
                resolve();
            },
            (error: unknown) => {
                clearTimeout(timer);
                reject(error instanceof Error ? error : new Error(String(error)));
            },
        );
    });
}
